import React from 'react';

export const cx = (...classes) => classes.filter(Boolean).join(' ');

export const AdminPageShell = ({ title, description, actions, children }) => (
  <section className="space-y-6">
    <header className="rounded-3xl border border-slate-200/60 bg-gradient-to-r from-cyan-500 via-sky-500 to-indigo-500 p-6 text-white shadow-lg shadow-cyan-500/20">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">{title}</h1>
          {description ? <p className="mt-2 text-sm text-white/85 sm:text-base">{description}</p> : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
    </header>
    {children}
  </section>
);

export const AdminCard = ({ className, children }) => (
  <div
    className={cx(
      'rounded-2xl border border-slate-200/70 bg-white/95 p-5 shadow-sm shadow-slate-900/5 backdrop-blur',
      'dark:border-slate-700/70 dark:bg-slate-900/80',
      className
    )}
  >
    {children}
  </div>
);

export const AdminSectionTitle = ({ title, description }) => (
  <div className="mb-3">
    <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-50">{title}</h2>
    {description ? <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{description}</p> : null}
  </div>
);

export const AdminInput = ({ className, ...props }) => (
  <input
    {...props}
    className={cx(
      'w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition',
      'placeholder:text-slate-400 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100',
      'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:ring-cyan-900/40',
      className
    )}
  />
);

export const AdminSelect = ({ className, children, ...props }) => (
  <select
    {...props}
    className={cx(
      'w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition',
      'focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-cyan-900/40',
      '[&>option]:bg-white [&>option]:text-slate-900 dark:[&>option]:bg-slate-900 dark:[&>option]:text-slate-100',
      className
    )}
  >
    {children}
  </select>
);

export const AdminButton = ({ className, variant = 'primary', ...props }) => {
  const variants = {
    primary: 'bg-slate-900 text-white hover:bg-slate-800 dark:bg-cyan-500 dark:text-slate-950 dark:hover:bg-cyan-400',
    secondary: 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700',
    danger: 'bg-rose-600 text-white hover:bg-rose-500',
    ghost: 'bg-transparent text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800'
  };
  return (
    <button
      {...props}
      className={cx(
        'inline-flex items-center justify-center rounded-xl px-3.5 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-60',
        variants[variant],
        className
      )}
    />
  );
};

export const AdminStatusPill = ({ value }) => {
  const normalized = String(value || '').toLowerCase();
  const tone = normalized === 'active' || normalized === 'approved'
    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
    : normalized === 'pending'
      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
      : 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300';
  return <span className={cx('rounded-full px-2.5 py-1 text-xs font-semibold capitalize', tone)}>{value || '-'}</span>;
};

export const AdminTable = ({ columns, rows, emptyMessage = 'No records found.' }) => (
  <div className="overflow-hidden rounded-2xl border border-slate-200/70 dark:border-slate-700/70">
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-100/80 dark:bg-slate-800/80">
          <tr>
            {columns.map((column) => (
              <th key={column.key} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200/70 bg-white dark:divide-slate-700/70 dark:bg-slate-900/40">
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-6 text-center text-slate-500 dark:text-slate-400">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((row, idx) => (
              <tr key={row.id || idx} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/50">
                {columns.map((column) => (
                  <td key={`${row.id || idx}-${column.key}`} className="px-4 py-3 text-slate-700 dark:text-slate-200">
                    {typeof column.render === 'function' ? column.render(row) : row[column.key] ?? '-'}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);
